import React, {useEffect, useState, Suspense, lazy} from "react"
import {Router, Redirect} from "@reach/router"
import {gtTheme} from "./assets/shared/gtTheme"
import {ThemeProvider, makeStyles} from "@material-ui/core/styles"
import "mobx-react-lite/batchingOptOut"
// components
import Brands from "./pages/Brands"
import BrandsDiscovery from "./pages/Brands/Discovery"
import BrandsLocations from "./pages/Brands/Locations"
import LocationGroups from "./pages/LocationGroups"
import LGHome from "./pages/LocationGroups/LGHome"
import LGLocations from "./pages/LocationGroups/LGLocations"
import Navbar from "./pages/Main/Navbar"
import HeaderContainer from "./pages/Main/Header"
import "./App.scss"
import axios from "axios"
import config from "./config"
import {getCookieValue, URLPATHS} from "./utils/utils"
import {Spinner} from "./components/Spinner"
import InternalError from "./components/InternalError"
import {UseStores} from "../src/hooks/UseStores"
import {observer} from "mobx-react-lite"
import AppData from "./pages/Main/AppData"
import Footer from "./components/Footer"
import Modal from "./components/TermsAndConditionsModal"
import PoiSearch from "./pages/PoiSearch/index"
import MapContainer from "./pages/Main/MapContainer"
import {navigate} from "@reach/router"
import Backdrop from "@material-ui/core/Backdrop"
import SearchInput from "./components/SearchInput"

// lazy-loaded components
const Insights = lazy(() => import("./pages/Insights"))
const NoPage = lazy(() => import("./pages/Main/NoPage"))

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#FFF",
  },
}))

const backdropStyle = {
  background: "rgba(0,0,0,0.80)",
}

const App = function () {
  const [isError, setIsError] = useState(false)
  const [userName, setUserName] = useState("")
  const cookieValue = getCookieValue()
  const classes = useStyles()

  const {
    locGroupStore,
    disStore,
    gtMapStore,
    navStore,
    poiStore,
    backdropStore,
    authDataStore,
  } = UseStores()

  const handleGeoUpdate = (geo, path) => {
    if (geo.geoType === "POI") {
      navigate(`/poi?id=${geo.geoId}`)
      setTimeout(() => {
        poiStore.setPoiId(geo.geoId)
      }, 100)
    } else {
      gtMapStore.setMapObj({geo: geo})
      if (path === URLPATHS.DISCOVERYBRANDS) disStore.setGeoTimeObj({geo: geo})
      else if (path === URLPATHS.LOCATIONGROUPS) locGroupStore.setLocGeoTime({geo: geo})
    }
  }
  //making request to UL auth
  useEffect(() => {
    let authRetryCount = 0

    async function authenticateUser() {
      const fixed = config.api.APP_GQL_HOST
      const url = fixed + "auth"
      const headers = {"x-gt-session": cookieValue}
      axios
        .get(url, {headers})
        .then(function (response) {
          let res = response && response.data && response.data.data
          let {full_name, user_id, email, tenant_id, allowed_apps} = res

          authDataStore.setAvailableApps(allowed_apps)
          setUserName(full_name)
          window.heap.identify(user_id)
          window.heap.addUserProperties({email})
          locGroupStore.setTenantId(tenant_id)
          gtMapStore.setMapObj({tenantId: tenant_id, authToken: cookieValue})
          setUpBackDrop()
        })
        .catch(function (error) {
          const {status} = error.response
          localStorage.removeItem("backdrop")
          if (status === 401 || status === 403)
            window.location.assign(config.api.LOGIN_URL)
          else {
            console.error("APP.JS/LINE 99--Authentication failed")
            handleAuthError()
          }
        })
    }

    function setUpBackDrop() {
      const isSessionPresent = JSON.parse(localStorage.getItem("backdrop"))

      if (!isSessionPresent) {
        localStorage.setItem("backdrop", JSON.stringify(true))
        backdropStore.setIsOpen(true)
      }
    }

    function handleAuthError() {
      if (authRetryCount < 3) {
        console.warn("APP.JS/LINE 116--Trying again to make auth request")
        authRetryCount++
        authenticateUser()
      } else {
        console.error("APP.JS/LINE 121--Authentication failed after three retries")
        setIsError(true)
      }
    }

    //if session value from cookie not present, redirect to the login url
    if (!cookieValue) {
      console.error("App.js LINE 70, no cookie")
      localStorage.removeItem("backdrop")
      window.location.assign(config.api.LOGIN_URL)
    } else {
      // Get User
      authenticateUser()
    }
  }, [])

  const handleCloseBackdrop = () => backdropStore.setIsOpen(false)

  return (
    <div className="App">
      {userName ? (
        <ThemeProvider theme={gtTheme}>
          <AppData />
          <HeaderContainer user={userName} />
          <Navbar />
          <Suspense fallback={<Spinner />}>
            <Router>
              <Brands path="/brands" handleGeoUpdate={handleGeoUpdate}>
                <BrandsDiscovery path="discovery" />
                <BrandsLocations path="locations" />
                <Redirect default noThrow from="/brands" to="/not-found" />
              </Brands>
              <Insights path="/insights" />
              <LocationGroups path="/locationgroups" handleGeoUpdate={handleGeoUpdate}>
                <LGHome path="/" />
                <LGLocations path="locations" />
                <Redirect default noThrow from="/locationgroups" to="/not-found" />
              </LocationGroups>
              <PoiSearch path="/poi" />
              <NoPage default />
              <Redirect noThrow from="/" to="/brands/discovery" />
            </Router>

            {navStore.currentPage === URLPATHS.LOCATIONGROUPS ||
            navStore.currentPage === URLPATHS.DISCOVERYBRANDS ||
            navStore.currentPage === URLPATHS.POI ? (
              <MapContainer
                handleGeoUpdate={handleGeoUpdate}
                geoObj={gtMapStore.mapObj.geo}
                categoryId={gtMapStore.mapObj.categoryId}
                brandId={gtMapStore.mapObj.brandId}
                visitDuration={gtMapStore.mapObj.visitDuration}
                stateDistribution={gtMapStore.mapObj.mapGeos.STATE}
                dmaDistribution={gtMapStore.mapObj.mapGeos.DMA}
                showLoader={gtMapStore.mapObj.showLoader}
                locationGroupId={gtMapStore.mapObj.locationGroupId}
                tenantId={gtMapStore.mapObj.tenantId}
                authToken={gtMapStore.mapObj.authToken}
                mapPadding={gtMapStore.mapObj.mapPadding}
              />
            ) : null}
            <Footer />
          </Suspense>
          <Modal />
          <Backdrop
            style={backdropStyle}
            className={classes.backdrop}
            open={backdropStore.isOpen}
          >
            <div className="search-container">
              <SearchInput placeholder="Search Brands, Location Groups, Categories and POI" />
              <div
                className="close-backdrop close-button"
                onClick={handleCloseBackdrop}
              ></div>
            </div>
          </Backdrop>
        </ThemeProvider>
      ) : isError ? (
        <InternalError />
      ) : (
        <Spinner />
      )}
    </div>
  )
}
export default observer(App)
